import { Link } from 'react-router-dom'
import type { Phase, PhaseScheduleEntry } from '@jw-av/content'

interface ModuleSummary {
  id: string
  title: string
  path: string
  roles: string[]
  stage: string
  slideCount: number
}

interface PhaseCardProps {
  index: number
  phase: Phase
  scheduleEntry: PhaseScheduleEntry | null
  modules: ModuleSummary[]
  activeRole: string
  completedIds: Set<string>
}

const MODE_LABEL: Record<Phase['mode'], string> = {
  online: 'Online',
  async: 'Async',
  'in-person': 'In-person',
}

export function PhaseCard({
  index,
  phase,
  scheduleEntry,
  modules,
  activeRole,
  completedIds,
}: PhaseCardProps) {
  const visibleModules =
    activeRole === 'all'
      ? modules
      : modules.filter((m) => m.roles.includes(activeRole))

  const window = scheduleEntry?.window ?? 'TBD'
  const completedCount = visibleModules.filter((m) => completedIds.has(m.id)).length

  return (
    <article className="learn-plan-step">
      <div className="learn-plan-step__rail">
        <span className="learn-plan-step__num" aria-label={`Step ${index + 1}`}>
          {index + 1}
        </span>
        <span className="learn-plan-step__line" aria-hidden="true" />
      </div>

      <div className="learn-plan-step__body">
        <div className="learn-plan-step__meta">
          <span className={`learn-plan-step__badge learn-plan-step__badge--${phase.mode}`}>
            {MODE_LABEL[phase.mode]}
          </span>
          <span className="learn-plan-step__window">{window}</span>
        </div>
        <h2 className="learn-plan-step__title">{phase.title}</h2>
        <p className="learn-plan-step__desc">{phase.description}</p>

        {phase.ctaText && (
          <div className="learn-plan-step__cta">
            <p>{phase.ctaText}</p>
            {scheduleEntry?.meetingLink && (
              <a
                href={scheduleEntry.meetingLink}
                className="learn-btn learn-btn--primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open meeting
              </a>
            )}
          </div>
        )}

        {visibleModules.length > 0 && (
          <div className="module-band">
            <div className="module-band__header">
              <span className="module-band__label">Modules</span>
              <span className="module-band__count">
                {completedCount} / {visibleModules.length} complete
              </span>
            </div>
            <ul className="module-grid">
              {visibleModules.map((m, mIdx) => (
                <li key={m.id}>
                  <Link to={m.path} className="module-tile">
                    <div className="module-tile__content">
                      <div className="module-tile__head">
                        <span className="module-tile__num" aria-label={`Module ${mIdx + 1}`}>
                          {mIdx + 1}
                        </span>
                        <span className="module-tile__stage-badge">{m.stage}</span>
                      </div>
                      <div className="module-tile__top">
                        <span className="module-tile__title">{m.title}</span>
                        {completedIds.has(m.id) && (
                          <span className="module-tile__badge">Done</span>
                        )}
                      </div>
                      <span className="module-tile__meta">{m.slideCount} slides</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {visibleModules.length === 0 && activeRole !== 'all' && (
          <p className="module-band__empty">
            No modules for this role in this phase yet.
          </p>
        )}
      </div>
    </article>
  )
}
